import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps, useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useCompanyUsers, useDeleteCompanyUser } from '@/services/api/hooks/companies';
import { useInvites, useReleaseCompanyInvite } from '@/services/api/hooks/invites';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import SiPlus from '@/icons/SiPlus';
import { User } from '@/services/api/commonTypes';
import { FunctionComponent, Suspense } from 'react';
import InviteUsersToCustomerModal from './InviteUsersToCustomerModal';
import { RoleType, useRole } from '@/services/authentication/hooks/user';

const ManageCustomerUsersModal: FunctionComponent<ModalProps<{ mspId: string; customerId: string; asCustomer: boolean }>> = props => {
  const asCustomer = props.props?.asCustomer;
  
  const ManageCustomerUsers = () => {
    const mspId = props.props?.mspId || '';
    const customerId = props.props?.customerId || '';
    
    const role = useRole();
    const isAdmin = role.type === RoleType.Admin;

    const [users] = useCompanyUsers(mspId, customerId);
    const [invites] = useInvites(mspId, customerId);

    const deleteUser = useDeleteCompanyUser();
    const releaseInvite = useReleaseCompanyInvite();
    
    const inviteCustomerModalProps = useModal<{ mspId: string; customerId: string }>();

    if (!props.props?.mspId) throw new Error('Missing partner id');
    if (!props.props?.customerId) throw new Error('Missing company id');

    const onDelete = async (id: string) => {
      await deleteUser.mutateAsync({ partnerId: mspId, companyId: customerId, id });
    };

    const errorObj = deleteUser.error as Error | undefined;
    const error: string = errorObj ? errorObj.message : '';

    const onCustomerInvite = () => inviteCustomerModalProps.open({ mspId, customerId });

    const onApprove = async (inviteId: string) => {
      await releaseInvite.mutateAsync({ partnerId: mspId, companyId: customerId, inviteId });
    };

    return (
      <>
        <InviteUsersToCustomerModal {...inviteCustomerModalProps} />
        <div className="mt-5 space-y-4">
          <Button onClick={onCustomerInvite} className="ml-auto">
            <SiPlus className="text-xl" /><span>{asCustomer ? "Customer Authorised Agents" : "Customer"}</span>
          </Button>

          {(invites?.items || []).map((invite: any) => {
            if (invite.redeemed) return null;
            return (
              <div className="flex justify-between space-x-3 items-center" key={invite.id}>
                <div>
                  <p className="font-medium text-gray-500">{invite.email}</p>
                  <div className="flex space-x-2 mt-1">
                    {invite.approvalStatus === 'Pending' && (
                      <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Pending Approval</span>
                    )}
                    {invite.approvalStatus === 'Approved' && (
                      <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded">Pending Redemption</span>
                    )}
                  </div>
                </div>

                {invite.approvalStatus === 'Pending' && isAdmin && (
                  <Button
                    className="h-fit"
                    onClick={() => onApprove(invite.id)}
                    loading={releaseInvite.isLoading}
                  >
                    Approve
                  </Button>
                )}
              </div>
            );
          })}

          {users.items.map((user: User) => (
            <div className="flex justify-between space-x-3 items-center" key={user.id}>
              <div>
                <p key={user.id} className="font-medium">
                  {user.givenName} {user.surname}
                </p>

                <p>{user.email}</p>
              </div>

              {error && <p className="text-red-500">{error}</p>}

              <Button
                className="h-fit"
                onClick={async () => {
                  await onDelete(user.id);
                }}
                loading={deleteUser.isLoading}
                variant={ButtonVariant.Destructive}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">{asCustomer ? "Authorised Agents" : "Invite Customer"}</h1>
        </Modal.Controls>

        <Suspense fallback={<Loading />}>
          <ManageCustomerUsers />
        </Suspense>
      </Card>
    </Modal>
  );
};

export default ManageCustomerUsersModal;
